import React, { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import {
  AmountInput,
  ConfirmationModal,
  IbanInput,
  Input,
  Loader,
  NavigationBlock,
  ServiceCartSelect,
  StyledColumn,
  StyledContent,
  StyledForm,
  StyledPageWrapper,
  Title,
} from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { errorTitle, findErrorByParam, isResponseErrorArray } from "@monorepo/helpers";
import {
  useAddTemplateMutation,
  useAddTemplateToCartMutation,
  useEditTemplateMutation,
  useGetTemplateQuery,
  usePaynowTemplateMutation,
} from "@monorepo/services/api/carts";
import { setActiveCart } from "@monorepo/slices/activeCart/activeCart";
import theme from "@monorepo/styles/theme";
import { ErrorCodeEnum, ErrorResponseIdEnum } from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { styled, Switch, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import { reverse } from "named-urls";

import { useFindServiceProvider } from "./useFindServiceProvider";

interface IFormData {
  name: string;
  recipient: string;
  iban: string;
  amount: string;
  purpose: string;
  cart_group_id?: string;
}

type TEditTemplateFormProps = {
  id: string;
};

const ButtonsWrapper = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column-reverse",
  alignItems: "stretch",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const EditTemplateForm: React.FC<TEditTemplateFormProps> = ({ id }) => {
  const { data: template, error, isFetching: templateIsFetching } = useGetTemplateQuery(id);
  const { t } = useTranslation();
  const [addTemplateToCart, { isLoading: addTemplateToCartIsLoading }] = useAddTemplateToCartMutation();
  const [addTemplate, { error: addTemplateError }] = useAddTemplateMutation();
  const [editTemplate, { isLoading: editTemplateIsLoading }] = useEditTemplateMutation();
  const [paynowTemplate, { isLoading: paynowTemplateIsLoading }] = usePaynowTemplateMutation();
  const methods = useForm<IFormData>({ mode: "all", reValidateMode: "onChange" });
  const [cartsShown, setCartsShown] = useState(true);
  const dispatch = useDispatch();
  const [ibanDefaultValue, setIbanDefaultValue] = useState<{ key?: string; value: string }>();

  const {
    findServiceProviderAction: findServiceProviderActionSubmit,
    altPayment: altPaymentSubmit,
    confirmAction: confirmActionSubmit,
    declineAction: declineActionSubmit,
  } = useFindServiceProvider(methods?.getValues?.("recipient"), id);

  const {
    findServiceProviderAction: findServiceProviderActionPaynow,
    altPayment: altPaymentPaynow,
    confirmAction: confirmActionPaynow,
    declineAction: declineActionPaynow,
  } = useFindServiceProvider(methods?.getValues?.("recipient"), id);

  const [amountDefaultValue, setAmountDefaultValue] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonsRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();
  const [action, setAction] = useState<() => void>();

  useEffect(() => {
    if (template) {
      setAmountDefaultValue(template.amount);
      setIbanDefaultValue({ value: template.iban });
      methods.reset();
    }
  }, [methods, template]);

  useEffect(() => {
    if (Array.isArray(addTemplateError) && addTemplateError[0]?.status === ErrorCodeEnum.CONFLICT.toString()) {
      setShowConfirmationModal(true);
    }
  }, [addTemplateError]);

  if (!template) {
    return (
      <StyledPageWrapper noJustify>
        <Loader />
      </StyledPageWrapper>
    );
  } else {
    const onSubmit =
      (isSubmitOnly: boolean = false) =>
      async (data: IFormData) => {
        const service = () =>
          editTemplate({
            iban: data.iban,
            name: data.name,
            recipient: data.recipient,
            amount: data.amount,
            purpose: data.purpose,
            recurring_payment: false,
            id,
          })
            .unwrap()
            .then(() => {
              if (!cartsShown) {
                toast.success(t("services.template_edited"));
                setAction(() => () => history.push(reverse(routeConfig.dashboard.services.templates.self)));
              } else {
                addTemplateToCart({
                  ...data,
                  cart_group_id: String(data.cart_group_id),
                  id,
                  recurring_payment: false,
                })
                  .unwrap()
                  .then((addResponse) => {
                    ReactGA.event("add_to_list", {
                      service_id: addResponse.data.attributes.service_id,
                    });

                    setAction(
                      () => () =>
                        history.push(
                          reverse(routeConfig.dashboard.payments.cartGroup.self, {
                            cartGroupId: addResponse.data.attributes.cart_group_id,
                          })
                        )
                    );
                  })
                  .catch((err) => {
                    if (isResponseErrorArray(err)) {
                      if (err[0]?.id === ErrorResponseIdEnum.LIMIT_EXCEEDED) {
                        toast.warning(`${errorTitle(err) + " " + t("services.template_not_added_to_cart")}`, {
                          toastId: err[0]?.id,
                        });
                      } else {
                        err.forEach((x) => {
                          toast.error(x.title);
                        });
                      }
                    }
                  });
              }
            })
            .catch((err) => {
              methods.setError("iban", { type: "manual", message: errorTitle(err) });
            });

        return isSubmitOnly ? service() : findServiceProviderActionSubmit(service, methods.getValues("recipient"));
      };

    const onPayNow =
      (isSubmitOnly: boolean = false) =>
      async (data: IFormData) => {
        const service = () =>
          editTemplate({
            iban: data.iban,
            name: data.name,
            recipient: data.recipient,
            amount: data.amount,
            purpose: data.purpose,
            recurring_payment: false,
            id,
          })
            .unwrap()
            .then(() => {
              if (cartsShown) {
                addTemplateToCart({
                  ...data,
                  cart_group_id: String(data.cart_group_id),
                  id,
                  recurring_payment: false,
                })
                  .unwrap()
                  .then(() => {
                    paynowTemplate({
                      template_id: template.id,
                      cart_group_id: String(data.cart_group_id),
                    })
                      .unwrap()
                      .then(() => {
                        dispatch(setActiveCart({ cartId: String(data.cart_group_id) }));
                        ReactGA.event("pay_now");

                        setAction(() => () => history.push(routeConfig.dashboard.payments.paymentOverview));
                      });
                  })
                  .catch((err) => {
                    if (isResponseErrorArray(err)) {
                      if (err[0]?.id === ErrorResponseIdEnum.LIMIT_EXCEEDED) {
                        toast.warning(`${errorTitle(err) + " " + t("services.template_not_added_to_cart")}`, {
                          toastId: err[0]?.id,
                        });
                      } else {
                        err.forEach((x) => {
                          toast.error(x.title);
                        });
                      }
                    }
                  });
              }
            })
            .catch((err) => {
              methods.setError("iban", { type: "manual", message: errorTitle(err) });
            });

        return isSubmitOnly ? service() : findServiceProviderActionPaynow(service, methods.getValues("recipient"));
      };

    const onBlur = async () => {
      const values = methods.watch() as IFormData;
      // check if iban value is not the same as default iban value
      if (values.iban && values.iban.replace(/\s/g, "") !== template.iban) {
        addTemplate({
          ...values,
          recurring_payment: false,
        })
          .unwrap()
          .catch((err) => {
            methods.setError("iban", {
              type: "onChange",
              message: String(errorTitle(err)),
            });
          });
      }
    };

    const renderConfirmationModal = () => {
      if (!!altPaymentSubmit?.length) {
        return (
          <ConfirmationModal
            wide
            open
            onAction={() => setAction(() => confirmActionSubmit)}
            onClose={() => {
              declineActionSubmit();
            }}
            onDecline={() => {
              declineActionSubmit();
              methods.handleSubmit(onSubmit(true))();
            }}
            confirmButtonText={t("templates.addToRecurringPayments")}
            declineButtonText={t("templates.addWithRegularPayment")}
          >
            {t("templates.alternativePaymentSuggestion")}
          </ConfirmationModal>
        );
      }

      if (!!altPaymentPaynow?.length) {
        return (
          <ConfirmationModal
            wide
            open
            onAction={() => setAction(() => confirmActionPaynow)}
            onClose={() => {
              declineActionPaynow();
            }}
            onDecline={() => {
              declineActionPaynow();
              methods.handleSubmit(onPayNow(true))();
            }}
            confirmButtonText={t("templates.addToRecurringPayments")}
            declineButtonText={t("templates.addWithRegularPayment")}
          >
            {t("templates.alternativePaymentSuggestion")}
          </ConfirmationModal>
        );
      }

      return null;
    };

    return (
      <>
        <NavigationBlock methods={methods} action={action} setAction={setAction} />
        <StyledPageWrapper noJustify>
          <Title>{t("services.edit_template")}</Title>
          <FormProvider {...methods}>
            <StyledForm onSubmit={methods.handleSubmit(onSubmit())}>
              <StyledContent>
                <StyledColumn>
                  <Input
                    defaultValue={template.name}
                    label={t("services.template_name")}
                    {...methods.register("name", {
                      required: {
                        value: true,
                        message: t("common.required"),
                      },
                    })}
                    error={
                      methods.formState.errors.name && !findErrorByParam({ error, param: "name" })
                        ? methods.formState.errors.name.message
                        : findErrorByParam({ error, param: "name" })
                    }
                  />
                  <Input
                    defaultValue={template.recipient}
                    label={t("services.recipient")}
                    {...methods.register("recipient", {
                      required: {
                        value: true,
                        message: t("common.required"),
                      },
                    })}
                    error={methods.formState.errors.recipient && methods.formState.errors.recipient.message}
                  />
                  <IbanInput control={methods.control} defaultValue={ibanDefaultValue} onBlurAction={onBlur} />
                </StyledColumn>
                <StyledColumn>
                  <AmountInput control={methods.control} defaultValue={amountDefaultValue} />
                  <Input
                    defaultValue={template.purpose}
                    label={t("services.purpose")}
                    {...methods.register("purpose", {
                      required: {
                        value: true,
                        message: t("common.required"),
                      },
                    })}
                    error={methods.formState.errors.purpose && methods.formState.errors.purpose.message}
                  />
                  <Box ref={containerRef}>
                    {t("cart.add_to_cart")}
                    <Switch
                      checked={cartsShown}
                      onChange={(e) => {
                        setCartsShown((prevState) => !prevState);
                        return e;
                      }}
                    />
                    <Slide appear={false} direction="left" in={cartsShown} container={containerRef.current}>
                      <div>
                        <ServiceCartSelect fieldName={"cart_group_id"} disabled={!cartsShown} />
                      </div>
                    </Slide>
                  </Box>
                </StyledColumn>
              </StyledContent>
              <Box ref={buttonsRef} sx={{ margin: "0 auto", width: "100%" }}>
                <Slide appear={false} direction="right" in={!cartsShown} container={buttonsRef.current}>
                  <ButtonsWrapper>
                    <Tooltip title={String(t("services.must_add_to_cart_to_pay_now"))} disableInteractive>
                      {/* span is needed here to prevent issues in firefox and safari */}
                      <span>
                        <LoadingButton
                          loading={templateIsFetching}
                          disabled
                          sx={{ width: "100%", display: cartsShown ? "none" : "inherit" }}
                          disableElevation
                          color="secondary"
                          variant="contained"
                          onClick={(e) => {
                            methods.handleSubmit(onPayNow())(e);
                          }}
                        >
                          {t("payments.pay_now")}
                        </LoadingButton>
                      </span>
                    </Tooltip>
                    <LoadingButton
                      loading={templateIsFetching || editTemplateIsLoading}
                      sx={{ display: cartsShown ? "none" : "inherit" }}
                      disabled={
                        cartsShown ||
                        !methods.formState.isValid ||
                        editTemplateIsLoading ||
                        methods.formState.isSubmitting
                      }
                      disableElevation
                      variant="contained"
                      type="submit"
                    >
                      {t("services.save_template")}
                    </LoadingButton>
                  </ButtonsWrapper>
                </Slide>
                <Slide appear={false} direction="left" in={cartsShown} container={buttonsRef.current}>
                  <ButtonsWrapper>
                    <LoadingButton
                      loading={
                        templateIsFetching ||
                        editTemplateIsLoading ||
                        addTemplateToCartIsLoading ||
                        paynowTemplateIsLoading
                      }
                      sx={{ display: !cartsShown ? "none" : "inherit" }}
                      disabled={
                        !methods.formState.isValid ||
                        !cartsShown ||
                        templateIsFetching ||
                        methods.formState.isSubmitting ||
                        editTemplateIsLoading ||
                        addTemplateToCartIsLoading ||
                        paynowTemplateIsLoading
                      }
                      disableElevation
                      color="secondary"
                      variant="contained"
                      onClick={(e) => {
                        methods.handleSubmit(onPayNow())(e);
                      }}
                    >
                      {t("payments.pay_now")}
                    </LoadingButton>
                    <LoadingButton
                      loading={templateIsFetching || editTemplateIsLoading || addTemplateToCartIsLoading}
                      sx={{ display: !cartsShown ? "none" : "inherit" }}
                      disabled={
                        !methods.formState.isValid ||
                        !cartsShown ||
                        templateIsFetching ||
                        editTemplateIsLoading ||
                        addTemplateToCartIsLoading
                      }
                      disableElevation
                      variant="contained"
                      type="submit"
                    >
                      {t("cart.add_to_cart")}
                    </LoadingButton>
                  </ButtonsWrapper>
                </Slide>
              </Box>
            </StyledForm>
          </FormProvider>
        </StyledPageWrapper>
        {renderConfirmationModal()}
        {showConfirmationModal && addTemplateError && Array.isArray(addTemplateError) && (
          <ConfirmationModal
            open={showConfirmationModal}
            onAction={async () => {
              setShowConfirmationModal(false);
              const values = methods.getValues() as IFormData;
              const templateId = addTemplateError[0]?.source?.id as string;
              editTemplate({
                ...values,
                recurring_payment: false,
                id: templateId,
              });
              if (values.cart_group_id) {
                addTemplateToCart({
                  ...values,
                  cart_group_id: values.cart_group_id,
                  recurring_payment: false,
                  id: templateId,
                })
                  .unwrap()
                  .then(() => {
                    setAction(
                      () => () =>
                        history.push(
                          reverse(routeConfig.dashboard.payments.cartGroup.self, {
                            cartGroupId: String(values.cart_group_id),
                          })
                        )
                    );
                  })
                  .catch(() => {
                    setAction(() => () => history.push(reverse(routeConfig.dashboard.services.templates.self)));
                  });
              }
            }}
            onClose={() => {
              setShowConfirmationModal(false);
              const ibanValue = `${methods.getValues("iban")}`;
              const updatedIban = `${ibanValue.substring(0, ibanValue.length - 1)}`;
              // key forces defaultIbanValue update each time
              setIbanDefaultValue({ key: String(Math.random()), value: updatedIban });
              methods.clearErrors("iban");
            }}
          >
            {addTemplateError[0].title}
          </ConfirmationModal>
        )}
      </>
    );
  }
};

export default EditTemplateForm;
